import React from "react";
import { cloneDeep } from "lodash";
import { Content } from "./Content";
import PropTypes from "prop-types";
import { MainHeader } from "./MainHeader";
import { Legend } from "./Legend";
import { Footer, Submit } from "../Footer";
import {
  DialysisDragDrop,
  PredictionFeedback,
} from "@learningblocks/storybook-components";
import "./style.scss";

/**
 * DialysisDragDrop Question
 *  - Prediction Quesiton to select dialysis tube positions
 */
export function DialysisDragDropQuestion(props) {
  const FEEDBACK_MESSAGE = {
    SUCCESS: (
      <p>
        Prediction submitted. You will see this again in your results. If you
        want to change your prediction, you can{" "}
        <button
          onClick={(e) => {
            e.preventDefault();
            props.onReset({
              id: props.id,
              type: "predicts",
            });
          }}
        >
          reset
        </button>{" "}
        it.
      </p>
    ),
    FAIL: (
      <p>
        Don't forget to drag all the dialysis tubes! If you want to change your
        prediction, you can{" "}
        <button
          onClick={(e) => {
            e.preventDefault();
            props.onReset({
              id: props.id,
              type: "predicts",
            });
          }}
        >
          reset
        </button>{" "}
        it.
      </p>
    ),
  };

  const getMessage = () => {
    let message = "";
    if (props.feedbackType === "SUCCESS") {
      message = FEEDBACK_MESSAGE.SUCCESS;
    }
    if (props.feedbackType === "FAIL") {
      message = FEEDBACK_MESSAGE.FAIL;
    }
    return message;
  };

  const getText = () => {
    if (props.dialysisConcentration.concentration === 0) return props.text;
    else {
      const text = props.text;
      text.value = text.value.replaceAll("0.0 M", "1.0 M");
      return text;
    }
  };

  const mergeDialysisTable = (
    dialysisTableData,
    dialysisTablePredictionData
  ) => {
    const dialysisTablePrediction = dialysisTablePredictionData;
    const dialysisTable = cloneDeep(dialysisTableData);

    dialysisTablePrediction?.responses?.forEach((response) => {
      if (response.cellId !== 0) {
        const originalCell = dialysisTable[
          response.colId
        ].colItems[0].cellItems.splice(response.dialysisId, 1);
        dialysisTable[response.colId].colItems[
          response.cellId
        ].cellItems.splice(response.cellId, 0, originalCell[0]);
      }
      dialysisTable[response.colId].colItems[response.cellId].cellItems[
        response.dialysisId
      ].selected = response.selected;
      if (response.value) {
        dialysisTable[response.colId].colItems[response.cellId].cellItems[
          response.dialysisId
        ].value = response.value;
      }
    });

    return dialysisTable;
  };

  return (
    <div className="dialysisdragdropquestion">
      <MainHeader heading={props.heading} text={getText()} />
      <Content>
        <DialysisDragDrop
          id={props.id}
          theme={props.dialysisTheme}
          style={props.getlStyle}
          dialysisRange={props.dialysisRange}
          dialysisTable={mergeDialysisTable(
            props.dialysisTable,
            props.dialysisTablePrediction
          )}
          onDialysisUpdate={props.onDialysisUpdate}
          onDialysisSelect={props.onDialysisSelect}
          feedbackType={props.feedbackType}
          responses={props.dialysisTablePrediction}
          xAxisLabel={props.xAxisLabel}
        />
      </Content>
      <PredictionFeedback
        type={props.feedbackType}
        onClose={
          props.feedbackType !== "SUCCESS"
            ? () => {
                props.closeFeedback({
                  id: props.id,
                  type: "predicts",
                });
              }
            : null
        }
        message={getMessage()}
        className="dialysisdragdropquestion_feedback"
      />
      <Legend />
      {props.feedbackType !== "SUCCESS" ? (
        <Footer>
          <Submit
            onSubmit={() => {
              props.onSubmit({
                id: props.id,
                type: "predicts",
                dialysisTable: mergeDialysisTable(
                  props.dialysisTable,
                  props.dialysisTablePrediction
                ),
              });
            }}
            disabled={!props.enableSubmit}
          >
            Submit prediction
          </Submit>
        </Footer>
      ) : null}
    </div>
  );
}

DialysisDragDropQuestion.propTypes = {
  /**
   * Unique Id for the component
   */
  id: PropTypes.number,
  /**
   * Main heading for the quetion
   */
  heading: PropTypes.string,
  /**
   * Detailed description for the question
   */
  text: PropTypes.string,
  /**
   * Function to invoke when submit button is clicked
   */
  onSubmit: PropTypes.func,
  /**
   * Function to invoke when clicking on reset button
   */
  onReset: PropTypes.func,
  /**
   * Boolean to set submit button enable/disable
   */
  enableSubmit: PropTypes.bool,
  /**
   * Type of feedback to show ['SUCCESS','FAIL', null]; if null no feedback is displayed
   */
  feedbackType: PropTypes.oneOf(["SUCCESS", "FAIL", ""]),
  /**
   * function to invoke when close feedback button clicked
   */
  closeFeedback: PropTypes.func,
  /**
   * Title to be displayed on top of DialysisDragDrop Component
   */
  dialysisTitle: PropTypes.string,
  /**
   * The bottom and top limit values of the dialysis slide
   */
  dialysisRange: PropTypes.shape({
    /**
     * The lower limit of range
     */
    bottom: PropTypes.number,
    /**
     * The upper limit of range
     */
    top: PropTypes.number,
  }),
  /**
   * Data needed to render the DialysisTable
   */
  dialysisTable: PropTypes.arrayOf(PropTypes.object),
  /**
   *  Function to invoke on Dialysis position update
   */
  onDialysisUpdate: PropTypes.func,
  /**
   * Function to invoke when a Dialysis band is clicked to select it
   */
  onDialysisSelect: PropTypes.func,
  /**
   * Style object to set on application wrapper
   */
  getlStyle: PropTypes.object,
  /**
   * Theme for the component
   */
  dialysisTheme: PropTypes.oneOf(["LIGHT", "DARK"]),
};

DialysisDragDropQuestion.defaultProps = {
  dialysisTheme: "LIGHT",
  getlStyle: {},
};
